import React from 'react'
import { Form, Col } from 'react-bootstrap'
import constants from '../../../../config/constants'
import {
  toPersonName,
  isValidPersonName
} from '../../../../utility/stringUtils'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Role } from '../../../../config/enums'

interface AddMembersFormProps {
  isSubmitting: boolean
  inviteCallback: (
    firstName: string,
    lastName: string,
    email: string,
    role: Role
  ) => void
}

const msg = constants.inlineError
const schema = yup.object({
  firstName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', msg.auth.invalidFirstName, (value) => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', msg.auth.invalidLastName, (value) => {
      return isValidPersonName(value)
    }),
  email: yup
    .string()
    .email(msg.auth.invalidEmail)
    .required(msg.fieldRequired.replace('%s', 'Email')),
  role: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Role'))
    .test('ValidRole', msg.auth.invalidRole, (value) => {
      return [Role.member, Role.admin].indexOf(value) >= 0 ? true : false
    })
})

const AddMembersForm: React.FC<AddMembersFormProps> = ({
  isSubmitting,
  inviteCallback
}) => {
  return (
    <Formik
      validationSchema={schema}
      onSubmit={(values) => {
        inviteCallback(
          values.firstName,
          values.lastName,
          values.email,
          values.role
        )
      }}
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        role: Role.member
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-edit-modal"
                htmlFor="invite-first-name"
              >
                First Name
              </Form.Label>
              <Form.Control
                id="invite-first-name"
                type="text"
                name="firstName"
                placeholder="Enter first name"
                value={values.firstName}
                onBlur={handleBlur}
                isInvalid={touched.firstName && !!errors.firstName}
                isValid={touched.firstName && !errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fName = toPersonName(e.target.value)
                  setFieldValue('firstName', fName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-edit-modal"
                htmlFor="invite-last-name"
              >
                Last Name
              </Form.Label>
              <Form.Control
                id="invite-last-name"
                type="text"
                name="lastName"
                placeholder="Enter last name"
                value={values.lastName}
                onBlur={handleBlur}
                isInvalid={touched.lastName && !!errors.lastName}
                isValid={touched.lastName && !errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const lName = toPersonName(e.target.value)
                  setFieldValue('lastName', lName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Form.Label className="mochi-edit-modal" htmlFor="invite-email">
              Email
            </Form.Label>
            <Form.Control
              id="invite-email"
              type="email"
              name="email"
              placeholder="Enter email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && !!errors.email}
              isValid={touched.email && !errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="pt-4">
            <h6>Member's permissions</h6>
          </Form.Group>
          <Form.Group>
            <Form.Check
              type="radio"
              id="invite-admin"
              label="Admin (access to view and edit everything)"
              name="role"
              checked={values.role === Role.admin}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('role', Role.admin)
              }}
            />
            <Form.Check
              type="radio"
              id="invite-member"
              name="role"
              label="Member (can't add new members, can't see financials)"
              checked={values.role === Role.member}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('role', Role.member)
              }}
            />
            {touched.role && errors.role && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {errors.role}
              </div>
            )}
          </Form.Group>
          <div className="text-center pt-4">
            <button
              className="mochi-button orange"
              type="submit"
              data-testid="signup-submit-button"
              disabled={isSubmitting ? true : false}
            >
              {isSubmitting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default AddMembersForm
